import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { fetchTemplateDetails, TemplateDetailsResponse } from "../api/endpoints/fetchTemplates";
import { generateHtmlFromAcro } from "../data/acroHelper";

export interface UseAcroFormTemplateDetailsParams {
  templateId: string;
  userEmail: string;
}

export interface AcroTemplateDetailsResult {
  fileId: string;
  htmlContent: string;
  json?: unknown;
}

export function useAcroFormTemplateDetails(
  { templateId, userEmail }: UseAcroFormTemplateDetailsParams,
  options?: Omit<
    UseQueryOptions<AcroTemplateDetailsResult, Error, AcroTemplateDetailsResult, readonly unknown[]>,
    "queryKey" | "queryFn" | "enabled"
  >
): UseQueryResult<AcroTemplateDetailsResult, Error> {
  return useQuery<AcroTemplateDetailsResult, Error, AcroTemplateDetailsResult, readonly unknown[]>({
    queryKey: ["acroTemplateDetails", templateId] as const,
    queryFn: async (): Promise<AcroTemplateDetailsResult> => {
      // Align with XFA hook: use backend template details API
      const response: TemplateDetailsResponse = await fetchTemplateDetails(String(templateId), userEmail);
      const { fileId } = response.data;
      let htmlContent = response.data.htmlContent;
      let parsedJson: unknown | undefined = undefined;
      // For ACRO, backend may return JSON string; if so, generate HTML via helper
      try {
        // Attempt to parse content as JSON; if succeeds, it's ACRO JSON
        parsedJson = JSON.parse(htmlContent);
        htmlContent = generateHtmlFromAcro(parsedJson);
      } catch {
        // Not JSON; leave htmlContent as-is
      }
      // Keep JSON payload for ACRO consumers (parsed JSON when available)
      return { fileId, htmlContent, json: parsedJson ?? undefined };
    },
    enabled: Boolean(templateId),
    // Configure React Query to always call queryFn even when offline
    networkMode: 'always', // This ensures queryFn is called regardless of network status
    retry: false, // Disable automatic retries to let our custom offline logic handle it
    staleTime: 0, // Don't use stale data, always fetch fresh or from our IndexedDB
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (formerly cacheTime)
    ...options,
  });
}
